import axios from 'axios';
import '../config.js';

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    Authorization: `Bearer ${token}`,
  };
};

const base = (path) => `${global.config.url}${path}`;

const normalizePagedResponse = (data) => {
  if (Array.isArray(data)) {
    return { items: data, total: data.length };
  }

  if (!data || typeof data !== 'object') {
    return { items: [], total: 0 };
  }

  const items = Array.isArray(data.items)
    ? data.items
    : Array.isArray(data.results)
      ? data.results
      : Array.isArray(data.data)
        ? data.data
        : [];

  let totalRaw = Array.isArray(items) ? items.length : 0;

  if (data.totalRecords !== undefined && data.totalRecords !== null) {
    totalRaw = data.totalRecords;
  } else if (data.total !== undefined && data.total !== null) {
    totalRaw = data.total;
  } else if (data.count !== undefined && data.count !== null) {
    totalRaw = data.count;
  } else if (data.totalCount !== undefined && data.totalCount !== null) {
    totalRaw = data.totalCount;
  }

  const total = Number(totalRaw);

  return {
    items,
    total: Number.isFinite(total) ? total : Array.isArray(items) ? items.length : 0,
  };
};

const normalizePreviewResourcesResponse = (data) => {
  if (Array.isArray(data)) return data;

  if (!data || typeof data !== 'object') return [];

  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.results)) return data.results;

  if (Array.isArray(data.sections)) {
    const flattened = [];

    data.sections.forEach((section) => {
      const sectionTitleRaw = section && section.sectionTitle !== undefined && section.sectionTitle !== null ? String(section.sectionTitle) : '';
      const sectionKeyRaw = section && section.sectionKey !== undefined && section.sectionKey !== null ? String(section.sectionKey) : '';
      const category = String(sectionTitleRaw || sectionKeyRaw || 'Uncategorised').trim() || 'Uncategorised';

      const items = section && Array.isArray(section.items) ? section.items : [];
      items.forEach((item) => {
        flattened.push({
          ...item,
          category,
          sectionKey: sectionKeyRaw,
          sectionTitle: sectionTitleRaw,
        });
      });
    });

    return flattened;
  }

  return [];
};

export const getAdminResources = async ({
  portalScope,
  productScope,
  category,
  isActive,
  includeDeleted,
  page,
  pageSize,
  search,
} = {}) => {
  const params = new URLSearchParams();
  if (portalScope) params.set('portalScope', String(portalScope));
  if (productScope) params.set('productScope', String(productScope));
  if (category) params.set('category', String(category));
  if (isActive !== undefined && isActive !== null && String(isActive) !== '') params.set('isActive', String(isActive));
  if (includeDeleted !== undefined && includeDeleted !== null && String(includeDeleted) !== '') {
    params.set('includeDeleted', String(includeDeleted));
  }
  if (page) params.set('page', String(page));
  if (pageSize) params.set('pageSize', String(pageSize));
  if (search) params.set('search', String(search));

  const response = await axios.get(`${base('/resources/admin')}?${params.toString()}`, {
    headers: authHeaders(),
  });

  return normalizePagedResponse(response.data);
};

export const getResourceById = async (id) => {
  const safeId = encodeURIComponent(String(id));
  const response = await axios.get(base(`/resources/${safeId}`), {
    headers: authHeaders(),
  });
  return response.data;
};

export const createResource = async (payload) => {
  const response = await axios.post(base('/resources'), payload, {
    headers: {
      ...authHeaders(),
      'Content-Type': 'application/json;charset=UTF-8',
    },
  });
  return response.data;
};

export const updateResource = async (id, payload) => {
  const safeId = encodeURIComponent(String(id));
  const response = await axios.put(base(`/resources/${safeId}`), payload, {
    headers: {
      ...authHeaders(),
      'Content-Type': 'application/json;charset=UTF-8',
    },
  });
  return response.data;
};

export const patchResourceStatus = async (id, isActive) => {
  const safeId = encodeURIComponent(String(id));
  const response = await axios.patch(
    base(`/resources/${safeId}/status`),
    { isActive: Boolean(isActive) },
    {
      headers: {
        ...authHeaders(),
        'Content-Type': 'application/json;charset=UTF-8',
      },
    }
  );
  return response.data;
};

export const deleteResource = async (id) => {
  const safeId = encodeURIComponent(String(id));
  const response = await axios.delete(base(`/resources/${safeId}`), {
    headers: authHeaders(),
  });
  return response.data;
};

export const getUploadUrl = async ({ fileName, contentType, portalScope, productScope }) => {
  const response = await axios.post(
    base('/resources/upload-url'),
    {
      fileName: String(fileName || ''),
      contentType: String(contentType || 'application/octet-stream'),
      portalScope: String(portalScope || ''),
      productScope: String(productScope || ''),
    },
    {
      headers: {
        ...authHeaders(),
        'Content-Type': 'application/json;charset=UTF-8',
      },
    }
  );
  return response.data;
};

export const previewSupportResources = async ({ customerId, appProviderId, lms } = {}) => {
  const params = new URLSearchParams();
  if (customerId !== undefined && customerId !== null && String(customerId) !== '') params.set('customerId', String(customerId));
  if (appProviderId !== undefined && appProviderId !== null && String(appProviderId) !== '') params.set('appProviderId', String(appProviderId));
  if (lms) params.set('lms', String(lms));

  const response = await axios.get(`${base('/resources/support')}?${params.toString()}`, {
    headers: authHeaders(),
  });

  return normalizePreviewResourcesResponse(response.data);
};

export const previewInternalResources = async () => {
  const response = await axios.get(base('/resources/internal'), {
    headers: authHeaders(),
  });
  return normalizePreviewResourcesResponse(response.data);
};

const resourcesApi = {
  getAdminResources,
  getResourceById,
  createResource,
  updateResource,
  patchResourceStatus,
  deleteResource,
  getUploadUrl,
  previewSupportResources,
  previewInternalResources,
};

export default resourcesApi;
